import { cn } from '@/lib/utils';
import { ChevronDown, X } from 'lucide-react';
import * as React from 'react';

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';

export type FilterPopoverTriggerVisualProps = {
  /** Smaller pills (e.g. Regulatory watch "My references" filter bar). */
  size?: 'default' | 'sm';
  /**
   * Active pill look: `highlight` matches legacy yellow fill; `outline` uses a dark border on neutral bg.
   */
  activeAppearance?: 'highlight' | 'outline';
  /** Extra classes for the operator segment (e.g. muted text). */
  operatorClassName?: string;
};

interface FilterPopoverTriggerProps extends FilterPopoverTriggerVisualProps {
  label: string;
  isActive: boolean;
  /** Operator text to show between label and value (e.g., "is any of", "between") */
  operator?: string;
  /** Content to show in the value segment when active */
  activeContent?: React.ReactNode;
  onClear?: () => void;
  children: React.ReactNode;
  className?: string;
  /** Controlled popover open state (optional). */
  open?: boolean;
  /** Called when open state changes; use with `open` for controlled mode. */
  onOpenChange?: (open: boolean) => void;
}

export const FilterPopoverTrigger: React.FC<FilterPopoverTriggerProps> = ({
  label,
  isActive,
  operator,
  activeContent,
  onClear,
  children,
  className,
  open: openControlled,
  onOpenChange: onOpenChangeControlled,
  size = 'default',
  activeAppearance = 'highlight',
  operatorClassName,
}) => {
  const [openUncontrolled, setOpenUncontrolled] = React.useState(false);
  const isControlled = openControlled !== undefined;
  const open = isControlled ? openControlled : openUncontrolled;

  const handleOpenChange = (next: boolean) => {
    if (!isControlled) {
      setOpenUncontrolled(next);
    }
    onOpenChangeControlled?.(next);
  };

  const sm = size === 'sm';
  const inactiveBtnClass = sm
    ? 'gap-1 rounded-[100px] border border-input bg-background px-2 py-1 text-xs font-medium shadow-sm'
    : 'gap-1.5 rounded-[100px] border border-input bg-background px-3 py-1.5 text-sm font-medium shadow-sm';
  const inactiveChevronClass = sm ? 'h-3 w-3 shrink-0' : 'h-3.5 w-3.5 shrink-0';
  const segmentPad = sm ? 'px-2 py-1' : 'px-3 py-1.5';
  const operatorPad = sm ? 'px-1.5 py-1' : 'px-2 py-1.5';
  const clearPad = sm ? 'px-1.5 py-1' : 'px-2 py-1.5';
  const clearIconClass = sm ? 'h-3 w-3' : 'h-3.5 w-3.5';
  const activeShellClass =
    activeAppearance === 'outline'
      ? 'border-neutral-900 bg-background'
      : 'border-input bg-[#FFFADD]';

  // Single <Popover> root so toggling isActive during interaction doesn't
  // unmount/remount the popover (which could leave the overlay stuck).
  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      {!isActive ? (
        <PopoverTrigger asChild>
          <button
            className={cn(
              'inline-flex items-center',
              inactiveBtnClass,
              'text-foreground hover:bg-accent',
              'transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
              className
            )}
          >
            <span className="whitespace-nowrap">{label}</span>
            <ChevronDown className={cn(inactiveChevronClass, 'shrink-0')} />
          </button>
        </PopoverTrigger>
      ) : (
        <div
          className={cn(
            'inline-flex items-stretch rounded-[100px] border font-medium shadow-sm',
            sm ? 'text-xs' : 'text-sm',
            activeShellClass,
            'focus-within:ring-1 focus-within:ring-ring',
            className
          )}
        >
          {/* Label segment - not clickable */}
          <span
            className={cn(
              'inline-flex items-center gap-1.5 rounded-l-[100px]',
              segmentPad,
              'text-foreground'
            )}
          >
            <span className="whitespace-nowrap">{label}</span>
          </span>

          {/* Separator */}
          <div className="w-px bg-input self-stretch" />

          {/* Operator segment - not clickable */}
          {operator && (
            <>
              <span
                className={cn(
                  'inline-flex items-center whitespace-nowrap',
                  operatorPad,
                  'text-foreground text-sm',
                  sm && 'text-xs',
                  operatorClassName
                )}
              >
                {operator}
              </span>
              {/* Separator */}
              <div className="w-px bg-input self-stretch" />
            </>
          )}

          {/* Value segment - opens popover */}
          <PopoverTrigger asChild>
            <button
              className={cn(
                'inline-flex items-center gap-1.5 max-w-[220px]',
                segmentPad,
                'text-foreground hover:bg-accent transition-colors',
                'focus-visible:outline-none whitespace-nowrap min-w-0'
              )}
            >
              {activeContent}
            </button>
          </PopoverTrigger>

          {/* Separator */}
          <div className="w-px bg-input self-stretch" />

          {/* Clear button */}
          <button
            onClick={e => {
              e.stopPropagation();
              onClear?.();
            }}
            className={cn(
              'inline-flex items-center justify-center rounded-r-[100px]',
              clearPad,
              'text-muted-foreground hover:text-foreground hover:bg-accent transition-colors',
              'focus-visible:outline-none'
            )}
          >
            <X className={clearIconClass} />
          </button>
        </div>
      )}
      <PopoverContent
        className="w-auto min-w-[200px] p-0"
        align="start"
        sideOffset={4}
      >
        {children}
      </PopoverContent>
    </Popover>
  );
};
